import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { Plus, Phone, Mail, MapPin, Ruler, GripVertical } from "lucide-react";

const STAGES = [
  { id: "new_lead", label: "New Lead", color: "#3B82F6" },
  { id: "contacted", label: "Contacted", color: "#8B5CF6" },
  { id: "estimate_scheduled", label: "Est. Scheduled", color: "#F59E0B" },
  { id: "estimate_sent", label: "Est. Sent", color: "#EA580C" },
  { id: "waiting_approval", label: "Waiting Approval", color: "#EF4444" },
  { id: "approved", label: "Approved", color: "#10B981" },
  { id: "scheduled", label: "Scheduled", color: "#06B6D4" },
  { id: "in_progress", label: "In Progress", color: "#F97316" },
  { id: "completed", label: "Completed", color: "#22C55E" },
  { id: "paid", label: "Paid", color: "#16A34A" },
];

const FENCE_TYPES = ["wood", "cedar", "chain_link", "ornamental_iron", "vinyl"];

export default function PipelinePage() {
  const [leads, setLeads] = useState([]);
  const [showNewLead, setShowNewLead] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [newLead, setNewLead] = useState({ name: "", phone: "", email: "", address: "", fence_type: "", lead_source: "", project_notes: "" });

  const fetchLeads = useCallback(async () => {
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch (err) { console.error("Failed to load leads:", err); toast.error("Failed to load leads"); }
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);
  useEffect(() => {
    if (searchParams.get("new") === "true") {
      setShowNewLead(true);
      // Clear the URL param so the dialog doesn't reopen on navigation
      window.history.replaceState({}, "", "/pipeline");
    }
  }, [searchParams]);

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) return;
    const newStage = destination.droppableId;
    setLeads((prev) => prev.map((l) => l.id === draggableId ? { ...l, stage: newStage } : l));
    try {
      await api.put(`/leads/${draggableId}/stage`, { stage: newStage });
      toast.success("Lead moved");
    } catch {
      toast.error("Failed to update");
      fetchLeads();
    }
  };

  const handleCreateLead = async (e) => {
    e.preventDefault();
    if (!newLead.name.trim()) return;
    try {
      await api.post("/leads", newLead);
      toast.success("Lead created!");
      setShowNewLead(false);
      setNewLead({ name: "", phone: "", email: "", address: "", fence_type: "", lead_source: "", project_notes: "" });
      fetchLeads();
    } catch { toast.error("Failed to create lead"); }
  };

  const getStageLeads = (stageId) => leads.filter((l) => l.stage === stageId);

  return (
    <div className="space-y-4" data-testid="pipeline-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-zinc-900 tracking-tight">Pipeline</h1>
          <p className="text-sm text-zinc-500 mt-1">{leads.length} total leads</p>
        </div>
        <Button onClick={() => setShowNewLead(true)} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="add-lead-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Lead
        </Button>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex gap-3 overflow-x-auto pb-4 kanban-scroll" data-testid="kanban-board">
          {STAGES.map((stage) => (
            <Droppable key={stage.id} droppableId={stage.id}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`flex-shrink-0 w-72 sm:w-80 bg-zinc-50 border rounded-lg p-3 transition-colors ${
                    snapshot.isDraggingOver ? "border-orange-400 bg-orange-50/30" : "border-zinc-200"
                  }`}
                  data-testid={`kanban-column-${stage.id}`}
                >
                  <div className="flex items-center gap-2 mb-3 px-1">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
                    <span className="text-sm font-bold text-zinc-800">{stage.label}</span>
                    <Badge variant="secondary" className="text-xs bg-zinc-200 text-zinc-600 ml-auto">
                      {getStageLeads(stage.id).length}
                    </Badge>
                  </div>
                  <div className="space-y-2 min-h-[80px]">
                    {getStageLeads(stage.id).map((lead, index) => (
                      <Draggable key={lead.id} draggableId={lead.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`bg-white border rounded-lg p-3 cursor-pointer transition-all ${
                              snapshot.isDragging ? "border-orange-500 shadow-lg rotate-1" : "border-zinc-200 hover:border-orange-300"
                            }`}
                            onClick={() => navigate(`/leads/${lead.id}`)}
                            data-testid={`lead-card-${lead.id}`}
                          >
                            <div className="flex items-start gap-2">
                              <div {...provided.dragHandleProps} className="mt-1 text-zinc-300 hover:text-zinc-500 cursor-grab">
                                <GripVertical className="w-4 h-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-zinc-900 truncate">{lead.name}</p>
                                {lead.phone && (
                                  <div className="flex items-center gap-1 mt-1">
                                    <Phone className="w-3 h-3 text-zinc-400" />
                                    <span className="text-xs text-zinc-500 truncate">{lead.phone}</span>
                                  </div>
                                )}
                                {lead.address && (
                                  <div className="flex items-center gap-1 mt-0.5">
                                    <MapPin className="w-3 h-3 text-zinc-400" />
                                    <span className="text-xs text-zinc-500 truncate">{lead.address}</span>
                                  </div>
                                )}
                                <div className="flex items-center gap-2 mt-2">
                                  {lead.fence_type && (
                                    <Badge variant="secondary" className="text-xs bg-orange-50 text-orange-700 capitalize">
                                      {lead.fence_type.replace(/_/g, " ")}
                                    </Badge>
                                  )}
                                  {lead.fence_length > 0 && (
                                    <span className="text-xs text-zinc-400 flex items-center gap-0.5">
                                      <Ruler className="w-3 h-3" /> {lead.fence_length}ft
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                </div>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>

      {/* New Lead Dialog */}
      <Dialog open={showNewLead} onOpenChange={(open) => { setShowNewLead(open); if (!open) window.history.replaceState({}, "", "/pipeline"); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Add New Lead</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateLead} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-2">
                <Label className="text-sm font-medium">Name *</Label>
                <Input value={newLead.name} onChange={(e) => setNewLead({ ...newLead, name: e.target.value })} placeholder="Customer name" required data-testid="new-lead-name" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Phone</Label>
                <Input value={newLead.phone} onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })} placeholder="403-555-0123" data-testid="new-lead-phone" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Email</Label>
                <Input type="email" value={newLead.email} onChange={(e) => setNewLead({ ...newLead, email: e.target.value })} placeholder="email@example.com" data-testid="new-lead-email" />
              </div>
              <div className="col-span-2 space-y-2">
                <Label className="text-sm font-medium">Address</Label>
                <Input value={newLead.address} onChange={(e) => setNewLead({ ...newLead, address: e.target.value })} placeholder="Full address" data-testid="new-lead-address" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Fence Type</Label>
                <Select value={newLead.fence_type} onValueChange={(v) => setNewLead({ ...newLead, fence_type: v })}>
                  <SelectTrigger data-testid="new-lead-fence-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {FENCE_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Lead Source</Label>
                <Input value={newLead.lead_source} onChange={(e) => setNewLead({ ...newLead, lead_source: e.target.value })} placeholder="Referral, Google..." data-testid="new-lead-source" />
              </div>
              <div className="col-span-2 space-y-2">
                <Label className="text-sm font-medium">Project Notes</Label>
                <Textarea value={newLead.project_notes} onChange={(e) => setNewLead({ ...newLead, project_notes: e.target.value })} placeholder="Details about the project..." rows={3} data-testid="new-lead-notes" />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowNewLead(false)} className="flex-1">Cancel</Button>
              <Button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700 text-white" data-testid="create-lead-submit">Create Lead</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
